import teamImage from '@assets/images/header_accueil.png'
import ScrollToTopButton from '@components/common/ScrollToTopButton'
import { PageHero, SectionHeading, Reveal, Icon, ICONS, CtaBand, NAVY, GOLD_ICON, GOLD } from '@components/marketing'

const VALUES = [
  { icon: 'shield', title: 'Confiance', text: 'Annonces modérées et échanges transparents pour que chacun achète et vende en toute sérénité.' },
  { icon: 'bolt', title: 'Innovation', text: 'Vidéos, bannières, lives : nous construisons les outils qui font vendre aujourd\'hui.' },
  { icon: 'users', title: 'Communauté', text: 'Une communauté solidaire d\'acheteurs, de vendeurs et d\'entreprises qui se font confiance.' },
  { icon: 'globe', title: 'Accessibilité', text: 'Une plateforme simple, sur le web et le mobile, accessible partout en Côte d\'Ivoire.' },
]

const STATS = [
  { value: '500+', label: 'Utilisateurs actifs' },
  { value: '1 000+', label: 'Annonces publiées' },
  { value: '98%', label: 'Clients satisfaits' },
  { value: '7j/7', label: 'Support disponible' },
]

const TIMELINE = [
  { year: '2024', title: 'La naissance', text: 'KUDHI est fondée par Unloader Technologies à Grand Bassam, avec une idée simple : rendre les échanges commerciaux locaux plus accessibles et transparents.' },
  { year: 'Aujourd\'hui', title: 'La plateforme de référence', text: 'Particuliers et entreprises se connectent chaque jour sur KUDHI, de l\'automobile à l\'immobilier en passant par la mode, l\'électronique et les services.' },
  { year: 'Demain', title: 'Connecter tout le pays', text: 'Créer des opportunités économiques pour chacun et contribuer au développement du commerce digital en Côte d\'Ivoire.' },
]

function AboutPage() {
  return (
    <div className="home-font bg-white text-slate-900 overflow-x-hidden">
      <PageHero
        badge="Fondée en 2024 à Grand Bassam"
        title="Rapprocher vendeurs"
        highlight="et acheteurs."
        subtitle="KUDHI est la plateforme de mise en relation entre vendeurs et acheteurs, entreprises et particuliers en Côte d'Ivoire."
      />

      {/* Mission */}
      <section className="py-20 lg:py-28">
        <div className="w-[90%] md:w-[80%] mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="overflow-hidden rounded-md shadow-2xl">
              <img src={teamImage} alt="L'équipe KUDHI" className="w-full h-[380px] lg:h-[480px] object-cover object-[65%_center]" />
            </div>
            <div className="absolute -bottom-6 left-6 flex items-center gap-3 rounded-md bg-white px-5 py-4 shadow-2xl">
              <span className="flex h-11 w-11 items-center justify-center rounded-md" style={GOLD_ICON}>
                <Icon d={ICONS.pin} />
              </span>
              <div>
                <p className="font-bold" style={{ color: NAVY }}>Grand Bassam</p>
                <p className="text-sm text-slate-500">Côte d&apos;Ivoire</p>
              </div>
            </div>
          </Reveal>

          <div>
            <SectionHeading eyebrow="Notre mission" title="Un espace où échanger en toute confiance." />
            <Reveal delay={100} className="mt-6 space-y-4 text-lg text-slate-600 leading-relaxed">
              <p>
                KUDHI est née d&apos;une vision simple mais ambitieuse : faciliter la mise en relation entre vendeurs et acheteurs,
                entreprises et particuliers, grâce à une plateforme moderne, sécurisée et accessible à tous.
              </p>
              <p>
                Notre mission est de connecter les Ivoiriens, de créer des opportunités économiques pour les particuliers
                et les entreprises, et de contribuer au développement du commerce digital dans notre pays.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <SectionHeading eyebrow="Nos valeurs" title="Ce qui nous guide au quotidien." center />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 80} className="group rounded-md border border-slate-200/70 bg-white p-8 transition-all hover:-translate-y-1 hover:shadow-xl hover:border-transparent">
                <span className="flex h-12 w-12 items-center justify-center rounded-md transition-colors" style={GOLD_ICON}>
                  <Icon d={ICONS[v.icon]} />
                </span>
                <h3 className="mt-6 text-xl font-bold" style={{ color: NAVY }}>{v.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Histoire */}
      <section className="py-20 lg:py-28">
        <div className="w-[90%] md:w-[80%] mx-auto grid lg:grid-cols-[1fr_1.4fr] gap-14">
          <SectionHeading eyebrow="Notre histoire" title="D'une idée locale à une plateforme nationale." />
          <ol className="relative border-l-2 border-dashed border-amber-200 ml-3 space-y-10">
            {TIMELINE.map((t, i) => (
              <Reveal as="li" key={t.title} delay={i * 100} className="relative pl-10">
                <span className="absolute -left-[13px] top-1 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white" style={{ background: i === 0 ? GOLD : NAVY }}>
                  <span className="h-2 w-2 rounded-full bg-white" />
                </span>
                <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>{t.year}</p>
                <h3 className="mt-1 text-xl font-bold" style={{ color: NAVY }}>{t.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{t.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>

        {/* Chiffres */}
        <div className="w-[90%] md:w-[80%] mx-auto mt-24">
          <Reveal className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-md bg-slate-200">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white px-6 py-10 text-center">
                <p className="text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>{s.value}</p>
                <p className="mt-2 text-sm font-medium text-slate-500">{s.label}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <CtaBand
        badge="Rejoignez-nous"
        title="Prêt à acheter et vendre sur KUDHI ?"
        text="Créez votre compte gratuitement et découvrez les annonces de toute la Côte d'Ivoire."
        secondary={{ to: '/kudhi-market', label: 'Découvrir le Market' }}
      />

      <ScrollToTopButton />
    </div>
  )
}

export default AboutPage
